/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/** Renders a consistent dashboard panel header with an icon, title, and optional actions. */

import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';

interface DashboardPanelHeaderProps {
    actions?: ReactNode;
    icon: LucideIcon;
    iconTooltip?: string;
    title: string;
}

function DashboardPanelHeader({
    actions,
    icon: Icon,
    iconTooltip,
    title,
}: DashboardPanelHeaderProps) {
    const icon = <Icon className="h-5 w-5" aria-hidden="true" />;

    return (
        <header
            className="dashboard-panel__header border-b px-5 py-3"
            style={{
                borderBottomColor: 'var(--status-border-color, color-mix(in srgb, var(--color-base-content) 12%, transparent))',
            }}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                    {iconTooltip ? (
                        <div className="tooltip tooltip-top tooltip-start shrink-0" data-tip={iconTooltip}>
                            {icon}
                        </div>
                    ) : (
                        <div className="shrink-0">{icon}</div>
                    )}
                    <div className="h-5 w-px shrink-0 bg-base-content/20" aria-hidden="true" />
                    <h3 className="mb-0 truncate">{title}</h3>
                </div>
                {actions ? (
                    <div className="flex shrink-0 flex-wrap justify-end gap-2">
                        {actions}
                    </div>
                ) : null}
            </div>
        </header>
    );
}

export default DashboardPanelHeader;
